import { create } from "zustand";

// The v3.2 workspace's right pane on desktop: one docked column with a
// "Transcript | Assistant" switch. Which tab was last shown and whether the
// pane is open are remembered on this device (best effort: storage can be
// unavailable). `docked` is set by the workspace while the pane is a column
// (wide enough), so other parts (Sources' Replay) can show the transcript
// in it.

export type PaneTab = "transcript" | "assistant";
const KEY = "rosc.v3.sidePane";

const read = (): { tab: PaneTab; open: boolean } => {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? "null") as {
      tab?: unknown;
      open?: unknown;
    } | null;
    return {
      tab: value?.tab === "transcript" ? "transcript" : "assistant",
      open: value?.open === true,
    };
  } catch {
    return { tab: "assistant", open: false };
  }
};
const write = (tab: PaneTab, open: boolean): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ tab, open }));
  } catch {
    // Remembering is a convenience.
  }
};

export const useWorkspacePane = create<{
  tab: PaneTab;
  open: boolean;
  docked: boolean;
  show: (tab: PaneTab) => void;
  toggle: (tab: PaneTab) => void;
  hide: () => void;
  setDocked: (docked: boolean) => void;
}>()((set, get) => ({
  ...read(),
  docked: false,
  show: (tab) => {
    write(tab, true);
    set({ tab, open: true });
  },
  // Shows the tab, or collapses the pane when that tab is already showing.
  toggle: (tab) => {
    if (get().open && get().tab === tab) get().hide();
    else get().show(tab);
  },
  hide: () => {
    write(get().tab, false);
    set({ open: false });
  },
  setDocked: (docked) => {
    if (get().docked !== docked) set({ docked });
  },
}));
